import {
  WalletOutlined,
  GlobalOutlined,
  QrcodeOutlined,
  CreditCardOutlined,
  UserSwitchOutlined,
  FileTextOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal } from "antd";
import { useEffect, useRef } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { motion } from "framer-motion";

/* ── shared design tokens (navy/violet header + gold shimmer, matching the
   rest of the app's dialogs) ── */
const gradients = {
  header: "linear-gradient(120deg, #0B1F3D 0%, #1E3A8A 55%, #5B21B6 100%)",
  goldBar: "linear-gradient(90deg, #F5D06F, #B8860B 50%, #F5D06F)",
  invoiceChip: "linear-gradient(135deg, #334155, #0B1F3D)",
  totalChip: "linear-gradient(135deg, #60A5FA, #1D4ED8)",
  cashChip: "linear-gradient(135deg, #34D399, #059669)",
};

const labelStyle = {
  fontSize: "11px",
  fontWeight: 600,
  color: "#6b7280",
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  marginBottom: 3,
  display: "block",
};

/* color-coded palette per payment method — each gets a bright "vivid"
   gradient for its active/selected state, a soft tint for its detail
   panel, and its own icon so it's identifiable at a glance. */
const methodPalette = {
  CASH: {
    icon: <WalletOutlined />,
    border: "#bbf7d0",
    text: "#059669",
    tint: "linear-gradient(135deg, #ECFDF5, #D1FAE5)",
    vivid: "linear-gradient(135deg, #34D399, #059669)",
    solid: "#059669",
  },
  ONLINE: {
    icon: <GlobalOutlined />,
    border: "#a5f3fc",
    text: "#0e7490",
    tint: "linear-gradient(135deg, #ECFEFF, #CFFAFE)",
    vivid: "linear-gradient(135deg, #22D3EE, #0E7490)",
    solid: "#0e7490",
  },
  UPI: {
    icon: <QrcodeOutlined />,
    border: "#e9d5ff",
    text: "#7e22ce",
    tint: "linear-gradient(135deg, #FAF5FF, #F3E8FF)",
    vivid: "linear-gradient(135deg, #C084FC, #7E22CE)",
    solid: "#7e22ce",
  },
  CARD: {
    icon: <CreditCardOutlined />,
    border: "#bfdbfe",
    text: "#1d4ed8",
    tint: "linear-gradient(135deg, #EFF6FF, #DBEAFE)",
    vivid: "linear-gradient(135deg, #60A5FA, #1D4ED8)",
    solid: "#1d4ed8",
  },
  CREDIT: {
    icon: <UserSwitchOutlined />,
    border: "#fecdd3",
    text: "#be123c",
    tint: "linear-gradient(135deg, #FFF1F2, #FFE4E6)",
    vivid: "linear-gradient(135deg, #FB7185, #BE123C)",
    solid: "#be123c",
  },
};

const PaymentModesDialog = ({
  billNo,
  invNo,
  payMode,
  setPayMode,
  totalNetAmount,
  cashValue,
  setCashValue,
  cashAmt,
  setCashAmt,
  cardAmt,
  setCardAmt,
  upiAmt,
  setUpiAmt,
  onlineAmt,
  setOnlineAmt,
  creditAmt,
  setCreditAmt,
  userDetails,
  cardAcc,
  upiAcc,
  onlineAcc,
  setOpen,
  base64QR,
  cPhoneNo,
  setCPhoneNo,
  userDetailsAPI,
  smCodeRef,
}) => {
  // NOTE: these refs are unused in this component as originally written —
  // kept as-is since removing them wasn't part of the ask, but flagging in
  // case they were meant to wire up fields that aren't here yet.
  const selectPayModeRef = useRef(null);
  const accountNoRef = useRef(null);
  const modeOfPayRef = useRef(null);
  const fYearRef = useRef(null);
  const refNoRef = useRef(null);
  const particularsRef = useRef(null);
  const gwtRef = useRef(null);
  const lessRef = useRef(null);
  const nwtRef = useRef(null);
  const touchRef = useRef(null);
  const fineRef = useRef(null);
  const rateRef = useRef(null);
  const recAmtRef = useRef(null);
  const returnAmtRef = useRef(null);
  const submitBtnRef = useRef(null);

  const handleChange = (method) => {
    setCashValue(method);
    if (method === "CARD") {
      setCardAmt(totalNetAmount);
      setUpiAmt(0);
      setOnlineAmt(0);
      setCashAmt(0);
      setCreditAmt(0);
    }
    if (method === "UPI") {
      setUpiAmt(totalNetAmount);
      setOnlineAmt(0);
      setCardAmt(0);
      setCashAmt(0);
      setCreditAmt(0);
    }
    if (method === "ONLINE") {
      setOnlineAmt(totalNetAmount);
      setUpiAmt(0);
      setCardAmt(0);
      setCashAmt(0);
      setCreditAmt(0);
    }
    if (method === "CASH") {
      setCashAmt(totalNetAmount);
      setUpiAmt(0);
      setCardAmt(0);
      setOnlineAmt(0);
      setCreditAmt(0);
    }
    if (method === "CREDIT") {
      setCreditAmt(totalNetAmount);
      setUpiAmt(0);
      setCardAmt(0);
      setOnlineAmt(0);
      setCashAmt(0);
    }
  };

  useEffect(() => {
    const cashAmount =
      Number(totalNetAmount || 0) -
      Number(cardAmt || 0) -
      Number(upiAmt || 0) -
      Number(onlineAmt || 0) -
      Number(creditAmt || 0);

    setCashAmt(cashAmount >= 0 ? cashAmount : 0);
  }, [cardAmt, upiAmt, onlineAmt, creditAmt, totalNetAmount]);

  const handleLimitedInput = (e, setFunc) => {
    const value = e.target.value.replace(/\D/g, "");
    if (Number(value) > Number(totalNetAmount)) return;
    setFunc(Number(value));
  };

  /* fixed-width, centered amount input — was stretching to fill the panel
     before; now a compact, self-contained field */
  const panelInputStyle = (palette) => ({
    width: 220,
    padding: "12px 14px",
    borderRadius: 10,
    border: `1.5px solid ${palette.border}`,
    background: "#fff",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: 800,
    color: palette.text,
    outline: "none",
    boxShadow: `0 3px 10px ${palette.solid}22`,
  });

  const centerWrap = { display: "flex", justifyContent: "center" };

  const breakdown = [
    { key: "CASH", label: "Cash", value: cashAmt },
    { key: "CARD", label: "Card", value: cardAmt },
    { key: "UPI", label: "UPI", value: upiAmt },
    { key: "ONLINE", label: "Online", value: onlineAmt },
    { key: "CREDIT", label: "Credit", value: creditAmt },
  ];

  const collectedTotal =
    Number(cashAmt || 0) +
    Number(cardAmt || 0) +
    Number(upiAmt || 0) +
    Number(onlineAmt || 0) +
    Number(creditAmt || 0);
  const isTallied =
    Number(collectedTotal.toFixed(0)) === Number(Number(totalNetAmount || 0).toFixed(0));

  const activeMode = methodPalette[cashValue] || methodPalette.CASH;

  /* Reusable "stat chip" for the header row — Inv No / Mode / Total / Cash */
  const StatChip = ({ icon, chipGrad, label, value, onClick, index }) => (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.3 }}
      whileHover={onClick ? { y: -2, boxShadow: "0 10px 20px rgba(0,0,0,0.18)" } : {}}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: chipGrad,
        borderRadius: 12,
        padding: "8px 14px",
        boxShadow: "0 4px 14px rgba(0,0,0,0.14)",
        cursor: onClick ? "pointer" : "default",
        flex: 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: "rgba(255,255,255,0.75)", letterSpacing: "0.6px", textTransform: "uppercase" }}>
          {label}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value}
        </div>
      </div>
    </motion.div>
  );

  return (
    <Modal
      open={payMode}
      onCancel={() => {
        setPayMode(false);
        smCodeRef.current?.focus();
      }}
      footer={null}
      width="70%"
      centered
      closable={false}
      maskStyle={{ backdropFilter: "blur(6px)", backgroundColor: "rgba(11,15,30,0.35)" }}
      styles={{
        content: {
          padding: 0,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(30,27,75,0.35), 0 10px 24px rgba(0,0,0,0.15)",
        },
        body: { padding: 0, background: "#f8fafc" },
      }}
    >
      {/* ── title bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 15,
          fontWeight: 700,
          letterSpacing: "0.8px",
          textTransform: "uppercase",
          color: "white",
          padding: "14px 16px",
          background: gradients.header,
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
        }}
        onClick={() => {
          setPayMode(false);
          smCodeRef.current?.focus();
        }}
      >
        <motion.div
          style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, background: gradients.goldBar, backgroundSize: "200% 100%" }}
          animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
        <motion.span whileHover={{ x: -3 }} style={{ display: "flex" }}>
          <ArrowBackIcon style={{ fontSize: 18, color: "white" }} />
        </motion.span>
        <div style={{ flex: 1, textAlign: "center" }}>Payment Modes</div>
        <div style={{ width: 18 }} />
      </div>

      {/* ── header stat chips: Inv No / Mode / Total Amount / Cash Amount ── */}
      <div style={{ display: "flex", gap: 10, padding: "14px 16px 0" }}>
        <StatChip
          index={0}
          icon={<FileTextOutlined />}
          chipGrad={gradients.invoiceChip}
          label="Inv No"
          value={billNo ? billNo : invNo}
        />
        <StatChip
          index={1}
          icon={activeMode.icon}
          chipGrad={activeMode.vivid}
          label="Mode"
          value={cashValue || "—"}
        />
        <StatChip
          index={2}
          icon={<DollarCircleOutlined />}
          chipGrad={gradients.totalChip}
          label="Total Amount"
          value={`₹${Number(totalNetAmount || 0).toFixed(0)}`}
        />
        <StatChip
          index={3}
          icon={<WalletOutlined />}
          chipGrad={gradients.cashChip}
          label="Cash Amount"
          value={`₹${Number(cashAmt || 0).toFixed(0)}`}
          onClick={() => {
            handleChange("CASH");
            setCardAmt(0);
            setUpiAmt(0);
            setOnlineAmt(0);
            setCreditAmt(0);
          }}
        />
      </div>

      {/* ══════════════════════════════════════════════════════
          METHOD SELECT + DETAIL PANEL ROW
      ══════════════════════════════════════════════════════ */}
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "16px",
          background: "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #f5f0ff 100%)",
          position: "relative",
          overflow: "hidden",
          margin: "12px 16px 0",
          borderRadius: 12,
          minHeight: 220,
        }}
      >
        <div
          aria-hidden="true"
          style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4, #10b981)" }}
        />

        {/* method buttons — vivid gradient fill when active, colorful icon
            chip always visible even at rest */}
        <div style={{ width: "22%", display: "flex", flexDirection: "column", gap: 10, position: "relative", zIndex: 1 }}>
          {["CASH", "ONLINE", "UPI", "CARD", "CREDIT"].map((btn) => {
            const p = methodPalette[btn];
            const active = cashValue === btn;
            return (
              <motion.button
                key={btn}
                onClick={() => {
                  handleChange(btn);
                  if (btn !== "CASH") setCashAmt(0);
                }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "9px 12px",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  border: active ? "1.5px solid transparent" : `1.5px solid ${p.border}`,
                  background: active ? p.vivid : "#fff",
                  color: active ? "#fff" : p.text,
                  boxShadow: active ? `0 6px 16px ${p.solid}40` : "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: active ? "rgba(255,255,255,0.25)" : p.vivid,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  {p.icon}
                </span>
                {btn}
              </motion.button>
            );
          })}
        </div>

        {/* detail panel */}
        <div style={{ width: "100%", padding: 4, display: "flex", gap: 12, position: "relative", zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            {(cashValue === "CASH" || !cashValue) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  padding: 16,
                  borderRadius: 12,
                  background: methodPalette.CASH.tint,
                  border: `1.5px solid ${methodPalette.CASH.border}`,
                  borderTop: `3px solid ${methodPalette.CASH.solid}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={labelStyle}>Cash Payment</span>
                <span style={{ fontSize: 32, fontWeight: 800, color: methodPalette.CASH.text }}>
                  ₹{Number(cashAmt || 0).toLocaleString("en-IN")}
                </span>
                <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>Full amount collected in cash</span>
              </motion.div>
            )}

            {cashValue === "ONLINE" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  padding: 16,
                  borderRadius: 12,
                  background: methodPalette.ONLINE.tint,
                  border: `1.5px solid ${methodPalette.ONLINE.border}`,
                  borderTop: `3px solid ${methodPalette.ONLINE.solid}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, justifyContent: "center" }}>
                  <span style={labelStyle}>Acc.No</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: methodPalette.ONLINE.text }}>{onlineAcc}</span>
                </div>
                <div style={{ ...centerWrap, flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <label style={labelStyle}>Online Payment Amount</label>
                  <input
                    placeholder="Enter amount"
                    value={onlineAmt}
                    autoFocus={true}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      handleLimitedInput(e, setOnlineAmt);
                      setCardAmt(0);
                      setUpiAmt(0);
                      setCreditAmt(0);
                    }}
                    style={panelInputStyle(methodPalette.ONLINE)}
                  />
                </div>
              </motion.div>
            )}

            {cashValue === "UPI" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  padding: 16,
                  borderRadius: 12,
                  background: methodPalette.UPI.tint,
                  border: `1.5px solid ${methodPalette.UPI.border}`,
                  borderTop: `3px solid ${methodPalette.UPI.solid}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, justifyContent: "center" }}>
                  <span style={labelStyle}>Acc.No</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: methodPalette.UPI.text }}>{upiAcc}</span>
                </div>
                <div style={{ ...centerWrap, flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <label style={labelStyle}>UPI Payment Amount</label>
                  <input
                    placeholder="Enter amount"
                    value={upiAmt}
                    autoFocus={true}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      handleLimitedInput(e, setUpiAmt);
                      setCardAmt(0);
                      setOnlineAmt(0);
                      setCreditAmt(0);
                    }}
                    style={panelInputStyle(methodPalette.UPI)}
                  />
                </div>
              </motion.div>
            )}

            {cashValue === "CARD" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  padding: 16,
                  borderRadius: 12,
                  background: methodPalette.CARD.tint,
                  border: `1.5px solid ${methodPalette.CARD.border}`,
                  borderTop: `3px solid ${methodPalette.CARD.solid}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, justifyContent: "center" }}>
                  <span style={labelStyle}>Acc.No</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: methodPalette.CARD.text }}>{cardAcc}</span>
                </div>
                <div style={{ ...centerWrap, flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <label style={labelStyle}>Card Payment Amount</label>
                  <input
                    placeholder="Enter amount"
                    value={cardAmt}
                    autoFocus={true}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      handleLimitedInput(e, setCardAmt);
                      setOnlineAmt(0);
                      setUpiAmt(0);
                      setCreditAmt(0);
                    }}
                    style={panelInputStyle(methodPalette.CARD)}
                  />
                </div>
              </motion.div>
            )}

            {cashValue === "CREDIT" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  padding: 16,
                  borderRadius: 12,
                  background: methodPalette.CREDIT.tint,
                  border: `1.5px solid ${methodPalette.CREDIT.border}`,
                  borderTop: `3px solid ${methodPalette.CREDIT.solid}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <label style={labelStyle}>Credit Payment</label>
                <div style={{ display: "flex", flexDirection: "row", gap: 8, width: 260, marginBottom: 8, marginTop: 4 }}>
                  <input
                    placeholder="Enter Mobile"
                    style={{
                      flex: 1,
                      padding: 12,
                      border: `1.5px solid ${methodPalette.CREDIT.border}`,
                      borderRadius: 8,
                      background: "#fff",
                      fontSize: "15px",
                      fontWeight: 600,
                      textAlign: "center",
                      color: methodPalette.CREDIT.text,
                    }}
                    autoFocus={true}
                    onFocus={(e) => e.target.select()}
                    value={cPhoneNo}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 10) {
                        setCPhoneNo(value);
                        if (value.length === 10) {
                          userDetailsAPI(value);
                        }
                      }
                    }}
                  />
                  {Object.keys(userDetails).length <= 0 ? (
                    <motion.button
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.92 }}
                      onClick={() => setOpen(true)}
                      style={{
                        background: "linear-gradient(135deg, #475569, #1E293B)",
                        border: "none",
                        color: "white",
                        borderRadius: "50%",
                        width: 35,
                        height: 35,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        cursor: "pointer",
                      }}
                    >
                      <UserOutlined />
                    </motion.button>
                  ) : null}
                </div>
                <input
                  type="text"
                  placeholder="Cust Name"
                  disabled
                  style={{
                    width: 260,
                    padding: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    marginBottom: 8,
                    background: "#f8fafc",
                    fontSize: "15px",
                    fontWeight: 600,
                    textAlign: "center",
                    color: "#374151",
                  }}
                  value={userDetails?.Custname}
                />
                <label style={labelStyle}>Amount</label>
                <input
                  placeholder="Enter Amount"
                  value={creditAmt}
                  autoFocus={true}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    handleLimitedInput(e, setCreditAmt);
                    setOnlineAmt(0);
                    setUpiAmt(0);
                    setCardAmt(0);
                  }}
                  style={panelInputStyle(methodPalette.CREDIT)}
                />
              </motion.div>
            )}
          </div>

          {upiAmt > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff",
                borderRadius: 12,
                border: "1.5px solid #e9d5ff",
                padding: 10,
                minWidth: 160,
                gap: 6,
              }}
            >
              <img src={base64QR} alt="code" style={{ maxWidth: "140px" }} />
              <span style={{ fontSize: 10.5, fontWeight: 700, color: "#7e22ce", letterSpacing: "0.4px", textTransform: "uppercase" }}>
                Scan to Pay
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          BOTTOM CARD — Breakdown / Status / Actions / Settlement
      ══════════════════════════════════════════════════════ */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 10,
          justifyContent: "space-around",
          padding: "12px 16px 16px",
          background: "linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)",
        }}
      >
        {/* Payment breakdown panel */}
        <div
          style={{
            width: "32%",
            borderRadius: 12,
            background: "linear-gradient(150deg,rgba(240,253,250,0.98) 0%,rgba(224,242,255,0.95) 50%,rgba(219,234,254,0.90) 100%)",
            border: "1.5px solid rgba(14,165,233,0.45)",
            padding: "12px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 3px 14px rgba(14,165,233,0.10), inset 0 1px 0 rgba(255,255,255,0.80)",
          }}
        >
          <div
            aria-hidden="true"
            style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg,#0ea5e9,#38bdf8,#7dd3fc)" }}
          />
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.7px",
              textTransform: "uppercase",
              color: "#0369a1",
              borderLeft: "2px solid rgba(14,165,233,0.55)",
              paddingLeft: "6px",
              marginBottom: 2,
            }}
          >
            Payment Breakdown
          </span>

          {breakdown.map(({ key, label, value }) => {
            const p = methodPalette[key];
            const amt = Number(value || 0);
            return (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, opacity: amt > 0 ? 1 : 0.45 }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: p.vivid,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 9,
                    flexShrink: 0,
                  }}
                >
                  {p.icon}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", flex: 1 }}>{label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: p.text, fontVariantNumeric: "tabular-nums" }}>
                  {amt > 0 ? `₹ ${amt.toLocaleString("en-IN")}` : "—"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Payment status badge */}
        {(() => {
          const theme = isTallied
            ? {
              border: "1.5px solid rgba(134,239,172,0.70)",
              background: "linear-gradient(150deg,rgba(240,253,244,0.95) 0%,rgba(220,252,231,0.90) 100%)",
              boxShadow: "0 3px 14px rgba(22,163,74,0.15)",
              stripe: "linear-gradient(90deg,#16a34a,#22c55e,#4ade80)",
              text: "#15803d",
              rail: "rgba(22,163,74,0.60)",
              label: "Tallied",
            }
            : {
              border: "1.5px solid rgba(252,165,165,0.70)",
              background: "linear-gradient(150deg,rgba(255,242,242,0.95) 0%,rgba(254,226,226,0.90) 100%)",
              boxShadow: "0 3px 14px rgba(220,38,38,0.15)",
              stripe: "linear-gradient(90deg,#b91c1c,#dc2626,#f87171)",
              text: "#b91c1c",
              rail: "rgba(220,38,38,0.60)",
              label: "Pending",
            };

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "20%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: theme.border,
                background: theme.background,
                boxShadow: theme.boxShadow,
                gap: "6px",
                minWidth: "120px",
                flexShrink: 0,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: theme.stripe }} />
              <div
                style={{
                  fontSize: "0.60rem",
                  fontWeight: 700,
                  color: theme.text,
                  borderLeft: `2px solid ${theme.rail}`,
                  paddingLeft: "6px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  alignSelf: "flex-start",
                  marginTop: "4px",
                }}
              >
                Payment Status
              </div>
              {isTallied ? (
                <CheckCircleOutlined style={{ fontSize: 38, color: theme.text }} />
              ) : (
                <CloseCircleOutlined style={{ fontSize: 38, color: theme.text }} />
              )}
              <div style={{ fontSize: "0.62rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.text }}>
                {theme.label}
              </div>
            </div>
          );
        })()}

        {/* Action buttons */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 10, alignItems: "center", width: "13%", padding: "0 8px" }}>
          <motion.button
            onClick={() => {
              setPayMode(false);
              setCardAmt(0);
              setUpiAmt(0);
              setOnlineAmt(0);
              setCreditAmt(0);
              setCashValue("CASH");
              setCashAmt(totalNetAmount);
              smCodeRef.current?.focus();
            }}
            whileHover={{ y: -2, boxShadow: "0 10px 22px rgba(190,18,60,0.35)", background: "linear-gradient(135deg, #FB7185, #BE123C)", color: "#fff" }}
            whileTap={{ scale: 0.96 }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              height: 40,
              width: 110,
              borderRadius: 10,
              border: "1.5px solid #fecaca",
              background: "#fff5f5",
              color: "#dc2626",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <CloseCircleOutlined /> Cancel
          </motion.button>

          <motion.button
            onClick={() => {
              setPayMode(false);
              smCodeRef.current?.focus();
            }}
            whileHover={{ y: -2, boxShadow: "0 10px 22px rgba(16,185,129,0.4)" }}
            whileTap={{ scale: 0.96, y: 0 }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              height: 40,
              width: 110,
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #34D399, #059669)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
            }}
          >
            <CheckCircleOutlined /> Save
          </motion.button>
        </div>

        {/* Settlement summary */}
        <div
          style={{
            minWidth: 220,
            borderRadius: 12,
            background: "linear-gradient(145deg, rgb(248,250,252), rgb(190,223,255))",
            border: "1px solid #e2e8f0",
            padding: "12px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            width: "30%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.7px", textTransform: "uppercase", color: "#94a3b8" }}>
            Settlement Summary
          </span>
          {[
            { label: "Collected", value: collectedTotal.toFixed(0), bg: "#f0fdf4", border: "#bbf7d0", color: "#059669" },
            { label: "Total Amount", value: Number(totalNetAmount || 0).toFixed(0), bg: "#eff6ff", border: "#bfdbfe", color: "#3b82f6" },
          ].map(({ label, value, bg, border, color }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", flex: 1 }}>{label}</span>
              <Input
                disabled
                value={value}
                style={{ width: 130, height: 32, borderRadius: 8, textAlign: "right", fontWeight: 700, fontSize: 14, background: bg, border: `1px solid ${border}`, color }}
              />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default PaymentModesDialog;